/**
 * Language context: provides locale and translation hooks to children.
 *
 * This context is now provided by LocaleLayout with language derived from the
 * route (via localeFromPathname), not from global i18n state or localStorage.
 * This enables SSG to emit separate language versions per route.
 *
 * setLanguage navigates to the equivalent URL in the other locale (e.g.
 * /en/services/implants → /ar/services/implants), not a client-side toggle.
 */

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Locale } from '@/content/types';
import { localizedPath } from '@/lib/locale';

interface LanguageContextType {
  language: Locale;
  setLanguage: (lang: Locale) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  language: Locale;
  i18n: any; // i18next instance passed from LocaleLayout
}

export const LanguageProvider = ({
  children,
  language,
  i18n,
}: LanguageProviderProps) => {
  const { t } = useTranslation(undefined, { i18n });
  const location = useLocation();
  const navigate = useNavigate();

  // Update document direction and language for client-side navigations.
  // Do NOT write to localStorage as source of truth — the URL is authoritative.
  useEffect(() => {
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const setLanguage = (lang: Locale) => {
    // Navigate to the equivalent path in the other locale.
    const newPath = localizedPath(lang, location.pathname);
    navigate(newPath);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider (via LocaleLayout)');
  }
  return context;
};
